import { useState, useEffect, useMemo, useCallback } from "react";
import { Grid } from "@mui/material";
import { GridRowParams, GridRowSelectionModel } from "@mui/x-data-grid";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { useDataGridPagination } from "~/hooks";
import { useAssetOutlet } from "../asset";
import { LoadingFailedPage } from "~/components/loading";

import {
  useGetFindingsQuery,
  FindingsOrderField,
  OrderDirection,
  FindingsOrder,
  ScoreState,
} from "~/operations";
import {
  FindingsDataGridTable,
  FindingRow,
  useDataGridTriageToolbar,
} from "~/components/FindingsTable";
import { getAssetMrn } from "~/lib/urlUtils";
import { buildFindingUrl } from "~/lib/findingUrl";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAssetAllFindingsFilterContext } from "./useAssetAllFindingsFilterContext";
import { SCORE_STATE_FILTER_KEY } from "./useAssetVulnerabilitiesFilterContext";
import { ALLOWED_FINDING_TYPES } from "./constants";

const PAGINATION_PARAMS = [
  "findings.after",
  "findings.page",
  "findings.pageSize",
] as const;

export function AssetFindingsTab() {
  const { assetId, isCicd, asset, space } = useAssetOutlet();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const jobId = searchParams.get("jobId") || "";
  const assetMrn = isCicd ? jobId : getAssetMrn(space.id, assetId);

  // pagination needs something to help determine when it should reset deps
  const filterResetKey = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    PAGINATION_PARAMS.forEach((key) => params.delete(key));
    return params.toString();
  }, [searchParams]);

  // Selection state for DataGrid
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({ type: "include", ids: new Set() });

  // Track connection metadata across renders for pagination stability
  const [connectionMeta, setConnectionMeta] = useState<{
    pageInfo?: { endCursor?: string | null; hasNextPage?: boolean };
    totalCount?: number;
  }>({});

  // Pagination
  const pagination = useDataGridPagination({
    prefix: "findings",
    defaultPageSize: 25,
    pageInfo: connectionMeta.pageInfo,
    totalCount: connectionMeta.totalCount,
    resetDeps: [filterResetKey],
  });

  // Helper to strip pagination cursor params from URLs (used by filters and sort)
  const resetCursorUrl = useCallback((href: string | URL) => {
    const url = new URL(href);
    PAGINATION_PARAMS.forEach((key) => url.searchParams.delete(key));
    return url;
  }, []);

  // Sorting
  const baseUrl = resetCursorUrl(location.href);
  const sort = useSort<FindingsOrder>({
    baseUrl,
    defaultSort: {
      field: FindingsOrderField.RiskValue,
      direction: OrderDirection.Desc,
    },
    validFields: [FindingsOrderField.RiskValue],
  });

  const baseQueryFilters = {
    includeCicd: isCicd,
    types: ALLOWED_FINDING_TYPES,
  };

  // Filters
  const { filterForm, filterVariables, activeFilterCount, resetFilters } =
    useAssetAllFindingsFilterContext({
      assetMrn: assetMrn,
      resetCursorUrl,
      baseQueryFilters,
    });

  useEffect(() => {
    // if no score state filter is set, default to open findings
    if (searchParams.get(SCORE_STATE_FILTER_KEY)) {
      return;
    }
    searchParams.set(SCORE_STATE_FILTER_KEY, ScoreState.Open);
    setSearchParams(searchParams, { replace: true });
  }, []);

  // Query
  const {
    data: _data,
    loading: isLoading,
    error,
  } = useGetFindingsQuery({
    variables: {
      scopeMrn: assetMrn,
      first: pagination.first,
      after: pagination.after,
      orderBy: sort.orderBy,
      filter: {
        ...baseQueryFilters,
        ...filterVariables,
      },
    },
    skip: !assetMrn,
    fetchPolicy: "cache-and-network",
  });

  const findingsUnion =
    _data?.findings?.__typename === "FindingsConnection"
      ? _data.findings
      : undefined;

  // Update connection metadata when data arrives
  useEffect(() => {
    if (findingsUnion) {
      setConnectionMeta({
        pageInfo: findingsUnion.pageInfo,
        totalCount: findingsUnion.totalCount,
      });
    }
  }, [findingsUnion]);

  // nexusStylePagination doesn't accumulate pages in the Apollo cache
  // (each cursor query is a separate cache entry), so this already
  // contains exactly the current page's items — no slicing needed.
  const findings = useMemo<FindingRow[]>(
    () =>
      (findingsUnion?.edges || []).flatMap((e) => {
        if (!e?.node) return [];
        const node = e.node;
        if (
          node.__typename === "CveFinding" ||
          node.__typename === "CheckFinding" ||
          node.__typename === "AdvisoryFinding"
        ) {
          return node;
        }
        if (node.__typename === "GenericFinding") {
          const { exceptions, ...rest } = node;
          return { ...rest, exception: exceptions?.[0] ?? null };
        }
        return [];
      }),
    [findingsUnion?.edges],
  );

  // Memoize selected MRNs to prevent infinite re-renders
  // Handle both "include" (specific rows selected) and "exclude" (all except these selected)
  const selectedMrns = useMemo(() => {
    if (rowSelectionModel.type === "include") {
      // Only these specific rows are selected
      return Array.from(rowSelectionModel.ids).map(String);
    } else {
      // type === "exclude" means all rows are selected except those in ids
      const excludedIds = new Set(
        Array.from(rowSelectionModel.ids).map(String),
      );
      return findings.map((f) => f.mrn).filter((mrn) => !excludedIds.has(mrn));
    }
  }, [rowSelectionModel, findings]);

  // Stable callback for clearing selection
  const handleSelectionClear = useCallback(() => {
    setRowSelectionModel({ type: "include", ids: new Set() });
  }, []);

  // Connect selection to triage toolbar
  useDataGridTriageToolbar({
    findings,
    selectedMrns,
    onSelectionClear: handleSelectionClear,
    entityMrn: assetMrn,
    assetId,
    assetName: asset?.name ?? undefined,
    assetMrn,
    totalCount: findingsUnion?.totalCount,
  });

  // Memoize filter key to avoid running JSON.stringify on every render
  const filterKey = useMemo(
    () => JSON.stringify(filterVariables),
    [filterVariables],
  );

  // Clear selection when filters change
  useEffect(() => {
    setRowSelectionModel({ type: "include", ids: new Set() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  if (error) {
    return <LoadingFailedPage what="findings" />;
  }

  const handleRowClick = (params: GridRowParams<FindingRow>) => {
    const url = buildFindingUrl(params.row, assetId, space.id, jobId);
    navigate(url);
  };

  return (
    <Grid container mt={3} rowGap={3}>
      <Grid size={12}>{filterForm}</Grid>

      <Grid size={12}>
        <FindingsDataGridTable
          rows={findings}
          loading={isLoading}
          onRowClick={handleRowClick}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          sort={sort}
          totalCount={findingsUnion?.totalCount}
          paginationModel={pagination.paginationModel}
          onPaginationModelChange={pagination.onPaginationModelChange}
          rowCount={pagination.rowCount}
          hiddenColumns={["assets"]}
          activeFilterCount={activeFilterCount}
          onResetFilters={resetFilters}
          assetId={assetId}
          spaceId={space.id}
          jobId={jobId}
          assetMrn={assetMrn}
          assetName={asset?.name ?? undefined}
        />
      </Grid>
    </Grid>
  );
}
